import {
  Grid,
  Box,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";

import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { useEffect, useState, useCallback } from "react";
import DatagridCustom from "../General/DatagridCustom";
import { getJudicialAccounts } from "../../services/JudicialAccountService";
import { formatDateTime } from "../../config/Helper";
import { useToast } from "../../context/ToastContext";

export default function ManagerJudicialAccounts() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const searchTerm = filterModel.quickFilterValues?.join(" ") || "";

      const res = await getJudicialAccounts({
        page: page + 1,
        perPage: pageSize,
        search: searchTerm,
      });

      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (error) {
      console.error("[ManagerJudicialAccounts] Error:", error);
      showToast(
        "No se pudieron cargar los datos. Intente de nuevo más tarde.",
      "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, paginationModel, filterModel]);

  const handleFilterModelChange = useCallback(
    (newFilterModel) => {
      const currentSearchTerm = filterModel.quickFilterValues?.join(" ") || "";
      const newSearchTerm = newFilterModel.quickFilterValues?.join(" ") || "";

      setFilterModel(newFilterModel);

      if (currentSearchTerm !== newSearchTerm) {
        setPaginationModel((prev) =>
          prev.page !== 0 ? { ...prev, page: 0 } : prev,
        );
      }
    },
    [filterModel],
  );

  const columns = [
    {
      field: "legal_file_code",
      headerName: "Expediente",
      flex: 1,
      renderCell: (params) => {
        const legalFileId = params.row.legal_file_id;
        const code = params.value || "—";

        if (!legalFileId) {
          return <Typography variant="body2">{code}</Typography>;
        }

        return (
          <Tooltip title="Ver detalle del expediente" placement="top">
            <Button
              component="a"
              href={`/legal/file/${legalFileId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {code}
            </Button>
          </Tooltip>
        );
      },
    },
    { field: "legal_file_name", headerName: "Carátula", flex: 2 },
    { field: "account_number", headerName: "Cuenta", flex: 1 },
    { field: "bank_branch_code", headerName: "Sucursal", flex: 0.7 },
    {
      field: "balance",
      headerName: "Saldo actual",
      flex: 1,
      align: "right",
      valueFormatter: (params) =>
        params == null || params === ""
          ? "—"
          : new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
              minimumFractionDigits: 2,
            }).format(Number(params)),
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.8,
      renderCell: (params) => {
        const v = String(params.value || "").toLowerCase();
        const label =
          v === "ok"
            ? "OK"
            : v === "inexistent"
              ? "INEXISTENTE"
              : v
                ? v.toUpperCase()
                : "—";

        return (
          <Chip
            size="small"
            label={label}
            sx={{
              height: 22,
              borderRadius: 999,
              fontWeight: 900,
              bgcolor:
                v === "ok"
                  ? "rgba(46,125,50,0.10)"
                  : v === "inexistent"
                    ? "rgba(211,47,47,0.10)"
                    : "rgba(0,0,0,0.06)",
              color:
                v === "ok"
                  ? "success.main"
                  : v === "inexistent"
                    ? "error.main"
                    : "text.primary",
              "& .MuiChip-label": { px: 1, fontSize: 12 },
            }}
          />
        );
      },
    },
    {
      field: "balance_at",
      headerName: "Fecha saldo",
      flex: 1,
      renderCell: (params) =>
        params.value ? formatDateTime(params.value) : "—",
    },
    {
      field: "last_uploaded_at",
      headerName: "Última carga",
      flex: 1,
      renderCell: (params) =>
        params.value ? formatDateTime(params.value) : "—",
    },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          component="a"
          href={`/legal/file/${params.row.legal_file_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <OpenInNewIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DatagridCustom
            rows={rows}
            columns={columns}
            title="Cuentas Judiciales"
            description="Saldos actuales por expediente (último registro, no sumatoria)."
            icon={<MonetizationOnRoundedIcon sx={{ fontSize: 32 }} />}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={total}
            loading={loading}
            filterModel={filterModel}
            onFilterChange={handleFilterModelChange}
            filterMode="server"
            isServerPagination={true}
            getRowId={(row) => row.id}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
